import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Menu, MenuItem } from '@mui/material';
import { useDispatch } from 'react-redux';
import PopupTitle from '../common/popup_title';
import { mealsAddedToMealBasket } from '../../features/end_user_shopping/end_user_shopping_slice';
import {
  useGetSharedBoardQuery,
  useGetSmorgBoardQuery,
  useLazyGetFullMealQuery,
  useLazyGetFullSharedMealQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  mealFromSharedMeal,
  mealWithScaledIngredientsReplaced,
} from '../../services/meals';
import { ServingsModal } from '../../features/end_user_shopping/components/servings_modal';
import { SHARED_BOARDS_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';
import { scaleMealIngredientsOperation } from '../../operations/meal_operations';
import { useGetCurrentSpaceMembershipQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import { MAX_MEALS_TO_ADD_TO_BASKET_AT_ONCE } from '../../features/end_user_shopping/services/shopping_lists';
import TooManyMealsErrorModal from '../../features/end_user_shopping/components/too_many_meals_error_modal';

const RecipeCollectionMenuActionsMenu = ({
  boardID,
  menuID,
  isSharedBoard,
  anchorEl,
  onDismiss,
}) => {
  const dispatch = useDispatch();

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const [lazyGetSharedMeal] = useLazyGetFullSharedMealQuery();

  const [servingsModalAnchorEl, setServingsModalAnchorEl] = useState(null);

  const { data: ownRecipesBoard } = useGetSmorgBoardQuery(boardID, {
    skip: !boardID || isSharedBoard,
  });

  const { data: sharedRecipesBoard } = useGetSharedBoardQuery(boardID, {
    skip: !boardID || !isSharedBoard,
    refetchOnMountOrArgChange: SHARED_BOARDS_REFRESH_INTERVAL_SECONDS,
  });

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const customerFoodDataID = customerFoodDataIDOfSpace(
    currentSpaceMembership?.spaceID,
  );

  const [isAddingMealsToBasket, setIsAddingMealsToBasket] = useState(false);

  const [isAddingTooManyMeals, setIsAddingTooManyMeals] = useState(false);

  const onDismissTooManyMealsErrorModal = () => {
    setIsAddingTooManyMeals(false);
    onDismiss();
  };

  const menu = (sharedRecipesBoard || ownRecipesBoard).menus.find(
    (m) => m.id === menuID,
  );
  const mealIDsToAddToBasket = (menu.sharedMealIDs || menu.mealIDs).filter(
    Boolean,
  );

  const handleClickAddAllMealsToBasket = (ev) => {
    if (mealIDsToAddToBasket.length > MAX_MEALS_TO_ADD_TO_BASKET_AT_ONCE) {
      setServingsModalAnchorEl(null);
      setIsAddingMealsToBasket(false);
      setIsAddingTooManyMeals(true);
      setServingsModalAnchorEl(null);
      return;
    }
    setServingsModalAnchorEl(ev.currentTarget);
  };

  const handleAddAllMealsToBasket = async (scaleToServings) => {
    setIsAddingMealsToBasket(true);
    try {
      if (mealIDsToAddToBasket.length > MAX_MEALS_TO_ADD_TO_BASKET_AT_ONCE) {
        setServingsModalAnchorEl(null);
        setIsAddingMealsToBasket(false);
        setIsAddingTooManyMeals(true);
        return;
      }
      const fullMealsToAddToBasket = await Promise.all(
        mealIDsToAddToBasket.map(async (mealID) => {
          let fullMeal;
          if (isSharedBoard) {
            const { data: fullSharedMeal } = await lazyGetSharedMeal(mealID);
            fullMeal = mealFromSharedMeal(fullSharedMeal);
            fullMeal.id = mealID;
          } else {
            const { data } = await lazyGetMeal(mealID);
            fullMeal = data;
          }
          return fullMeal;
        }),
      );
      const scaledMealsToAddToBasket = await Promise.all(
        fullMealsToAddToBasket.map(async (fullMeal) => {
          const scaleFactor =
            scaleToServings / (fullMeal.recipes[0]?.servings || 1);
          const scaledIngredients = await scaleMealIngredientsOperation(
            fullMeal,
            scaleFactor,
            customerFoodDataID,
          );
          return mealWithScaledIngredientsReplaced(
            fullMeal,
            scaleToServings,
            scaledIngredients,
            scaleFactor,
          );
        }),
      );
      dispatch(mealsAddedToMealBasket(scaledMealsToAddToBasket));
      onDismiss();
    } finally {
      setServingsModalAnchorEl(null);
      setIsAddingMealsToBasket(false);
    }
  };

  return (
    <>
      <Menu anchorEl={anchorEl} open onClose={onDismiss}>
        <PopupTitle
          titleText="Menu actions"
          closeButtonEnabled
          onClickClose={onDismiss}
        />
        <MenuItem onClick={handleClickAddAllMealsToBasket}>
          Add all meals to basket
        </MenuItem>
      </Menu>
      {servingsModalAnchorEl && (
        <ServingsModal
          anchorEl={servingsModalAnchorEl}
          isLoading={isAddingMealsToBasket}
          onSubmit={(scaleToServings) => {
            handleAddAllMealsToBasket(scaleToServings);
          }}
          onDismiss={() => setServingsModalAnchorEl(null)}
        />
      )}
      {isAddingTooManyMeals && (
        <TooManyMealsErrorModal onDismiss={onDismissTooManyMealsErrorModal} />
      )}
    </>
  );
};

RecipeCollectionMenuActionsMenu.propTypes = {
  boardID: PropTypes.string.isRequired,
  menuID: PropTypes.string.isRequired,
  isSharedBoard: PropTypes.bool.isRequired,
  anchorEl: PropTypes.element.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

export default RecipeCollectionMenuActionsMenu;
